import { SecureStorage } from '@aparajita/capacitor-secure-storage';
import { Capacitor } from '@capacitor/core';

const AUTH_TOKEN_KEY = 'auth_token';
const REFRESH_TOKEN_KEY = 'refresh_token';

/**
 * Composable for authentication token storage with platform-native secure storage
 * Uses iOS Keychain / Android Keystore on native platforms, localStorage on web
 */
export function useAuthStorage() {
  const isNative = Capacitor.isNativePlatform();

  /**
   * Store authentication token
   */
  async function setAuthToken(token: string): Promise<void> {
    if (isNative) {
      await SecureStorage.setItem(AUTH_TOKEN_KEY, token);
    } else {
      // Web: Use existing encrypted localStorage approach
      localStorage.setItem(AUTH_TOKEN_KEY, token);
    }
  }

  /**
   * Get authentication token
   */
  async function getAuthToken(): Promise<string | null> {
    if (isNative) {
      try {
        return await SecureStorage.getItem(AUTH_TOKEN_KEY);
      } catch (error) {
        // Key doesn't exist
        return null;
      }
    } else {
      return localStorage.getItem(AUTH_TOKEN_KEY);
    }
  }

  /**
   * Store refresh token
   */
  async function setRefreshToken(token: string): Promise<void> {
    if (isNative) {
      await SecureStorage.setItem(REFRESH_TOKEN_KEY, token);
    } else {
      localStorage.setItem(REFRESH_TOKEN_KEY, token);
    }
  }

  /**
   * Get refresh token
   */
  async function getRefreshToken(): Promise<string | null> {
    if (isNative) {
      try {
        return await SecureStorage.getItem(REFRESH_TOKEN_KEY);
      } catch (error) {
        return null;
      }
    } else {
      return localStorage.getItem(REFRESH_TOKEN_KEY);
    }
  }

  /**
   * Remove authentication tokens
   */
  async function removeAuthTokens(): Promise<void> {
    if (isNative) {
      try {
        await SecureStorage.removeItem(AUTH_TOKEN_KEY);
        await SecureStorage.removeItem(REFRESH_TOKEN_KEY);
      } catch (error) {
        // Keys may not exist, ignore errors
      }
    } else {
      localStorage.removeItem(AUTH_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
    }
  }

  return {
    setAuthToken,
    getAuthToken,
    setRefreshToken,
    getRefreshToken,
    removeAuthTokens,
    isNative
  };
}
